import {
  BOT_ACTION_LABELS,
  BOT_EVALUATION_STATUS_LABELS,
  BOT_INTENT_LABELS,
  BOT_SCENARIOS,
  estimateBatteryCost,
  SCENARIO_GROUP_LABELS,
  type BotEvaluationDto,
  type BotEvaluationScenarioDto,
  type BotEvaluationVariantResult,
  type BotEvaluationVariantSummary,
  type BotModelInfo,
  type BotModelsDto,
  type BotVariant,
  type ScenarioGroup,
} from '@performance-crm/shared';
import { FlaskConical, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { fmt } from '@/lib/format';
import {
  useAdminProjects,
  useBotEvaluation,
  useBotEvaluations,
  useBotKnowledge,
  useBotModels,
  useCreateBotEvaluation,
  useDeleteBotEvaluation,
} from '@/lib/queries';

const money = (usd: number) => (usd === 0 ? 'USD 0' : `USD ${usd.toFixed(4)}`);
const HAIKU = 'anthropic:claude-haiku-4-5-20251001';
const SONNET = 'anthropic:claude-sonnet-5';

interface Choice {
  variant: BotVariant;
  /** Modelos que correrían de verdad (null = reglas y plantillas o sin clave). */
  live: { understand: string | null; reply: string | null };
  note: string | null;
}

/** Catálogo de variantes a comparar, según los modelos del servidor y qué proveedores tienen clave. */
function choicesFor(
  models: BotModelsDto | undefined,
  custom: { understand: string; reply: string },
): Choice[] {
  if (!models) return [];
  const ready = new Set(
    models.providers.filter((p) => p.ready && p.id !== 'fake').map((p) => p.id),
  );
  const liveRef = (ref: string | null) => (ref && ready.has(ref.split(':')[0] ?? '') ? ref : null);
  const planLive = (plan: 'ESTANDAR' | 'PREMIUM') => ({
    understand: models.plans[plan].understand.fallbackToFake
      ? null
      : models.plans[plan].understand.requested,
    reply: models.plans[plan].reply.fallbackToFake ? null : models.plans[plan].reply.requested,
  });
  const noKey = (m: BotModelInfo) => m.fallbackToFake;
  const list: Choice[] = [
    {
      variant: { key: 'basico', label: 'Básico (reglas y plantillas)', plan: 'BASICO' },
      live: { understand: null, reply: null },
      note: null,
    },
    {
      variant: { key: 'estandar', label: 'Estándar (modelos del plan)', plan: 'ESTANDAR' },
      live: planLive('ESTANDAR'),
      note:
        noKey(models.plans.ESTANDAR.reply) || noKey(models.plans.ESTANDAR.understand)
          ? 'sin clave: corre simulado'
          : null,
    },
    {
      variant: { key: 'premium', label: 'Premium (modelos del plan)', plan: 'PREMIUM' },
      live: planLive('PREMIUM'),
      note: noKey(models.plans.PREMIUM.reply) ? 'sin clave: corre simulado' : null,
    },
    {
      variant: {
        key: 'haiku',
        label: 'Haiku en todo (más barato)',
        plan: 'ESTANDAR',
        models: { understand: HAIKU, reply: HAIKU },
      },
      live: { understand: liveRef(HAIKU), reply: liveRef(HAIKU) },
      note: ready.has('anthropic') ? null : 'sin clave: corre simulado',
    },
    {
      variant: {
        key: 'sonnet',
        label: 'Sonnet en todo',
        plan: 'ESTANDAR',
        models: { understand: SONNET, reply: SONNET },
      },
      live: { understand: liveRef(SONNET), reply: liveRef(SONNET) },
      note: ready.has('anthropic') ? null : 'sin clave: corre simulado',
    },
    {
      variant: {
        key: 'abierto',
        label: `Modelo abierto (${models.compatModel})`,
        plan: 'ESTANDAR',
        models: {
          understand: `compat:${models.compatModel}`,
          reply: `compat:${models.compatModel}`,
        },
      },
      live: {
        understand: liveRef(`compat:${models.compatModel}`),
        reply: liveRef(`compat:${models.compatModel}`),
      },
      note: ready.has('compat') ? null : 'sin servidor compatible: corre simulado',
    },
  ];
  if (custom.understand.trim() || custom.reply.trim())
    list.push({
      variant: {
        key: 'personalizado',
        label: 'Personalizado',
        plan: 'ESTANDAR',
        models: {
          understand: custom.understand.trim() || null,
          reply: custom.reply.trim() || null,
        },
      },
      live: {
        understand: liveRef(custom.understand.trim() || null),
        reply: liveRef(custom.reply.trim() || null),
      },
      note: null,
    });
  return list;
}

/** Batería de escenarios: corre las conversaciones escritas contra el bot con una o varias variantes y compara. */
export function BotEvaluation({ companyId }: { companyId: string }) {
  const { data: projects } = useAdminProjects(companyId);
  const { data: models } = useBotModels(companyId);
  const { data: evaluations } = useBotEvaluations(companyId);
  const create = useCreateBotEvaluation();
  const remove = useDeleteBotEvaluation();
  const toast = useToast();
  const [projectId, setProjectId] = useState('');
  const [selected, setSelected] = useState<string[]>(['basico', 'estandar']);
  const [custom, setCustom] = useState({ understand: '', reply: '' });
  const [accepted, setAccepted] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const activeProjects = (projects ?? []).filter((p) => p.active);
  useEffect(() => {
    if (!projectId && activeProjects[0]) setProjectId(activeProjects[0].id);
  }, [activeProjects, projectId]);
  const { data: knowledge } = useBotKnowledge(projectId || undefined);
  const choices = useMemo(() => choicesFor(models, custom), [models, custom]);
  const chars = knowledge?.text.length ?? 3000;
  const estimates = useMemo(
    () =>
      Object.fromEntries(
        choices.map((c) => [c.variant.key, estimateBatteryCost(BOT_SCENARIOS, chars, c.live)]),
      ),
    [choices, chars],
  );
  const chosen = choices.filter((c) => selected.includes(c.variant.key));
  const totalUsd = chosen.reduce((s, c) => s + (estimates[c.variant.key]?.usd ?? 0), 0);
  const needsOk = totalUsd > 0 && !accepted;
  const detailId = openId ?? evaluations?.items[0]?.id ?? null;
  const { data: detail } = useBotEvaluation(detailId);

  const run = async () => {
    try {
      const r = await create.mutateAsync({
        companyId,
        projectId,
        variants: chosen.map((c) => c.variant),
      });
      setOpenId(r.id);
      setAccepted(false);
      toast.success(
        'Batería en marcha',
        `${chosen.length} variante${chosen.length === 1 ? '' : 's'} × ${BOT_SCENARIOS.length} escenarios`,
      );
    } catch (err) {
      toast.error('No se pudo correr la batería', (err as Error).message);
    }
  };
  const toggle = (key: string) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  return (
    <Card data-testid="bot-evaluation">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FlaskConical className="text-primary h-4 w-4" /> Batería de escenarios y comparación de
          modelos
        </CardTitle>
        <CardDescription>
          {BOT_SCENARIOS.length} conversaciones escritas (calificación, preguntas, citas,
          objeciones, cortes y mensajes raros) se corren de corrido contra el bot con la ficha del
          proyecto. Cada variante muestra cuántos escenarios pasa, cuánto cuesta y cuánto demora. No
          toca leads ni WhatsApp.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 lg:grid-cols-[1fr_2fr]">
          <div className="space-y-1">
            <Label htmlFor="eval-project">Proyecto a evaluar</Label>
            <Select
              id="eval-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              data-testid="eval-project"
            >
              {activeProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Variantes a comparar</p>
            <div className="grid gap-1 sm:grid-cols-2" data-testid="eval-variants">
              {choices.map((c) => (
                <label key={c.variant.key} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={selected.includes(c.variant.key)}
                    onChange={() => toggle(c.variant.key)}
                    data-testid={`eval-variant-${c.variant.key}`}
                  />
                  <span>
                    {c.variant.label}
                    <span className="text-muted-foreground block text-xs">
                      {estimates[c.variant.key]?.usd
                        ? `aprox. ${money(estimates[c.variant.key]!.usd)}`
                        : 'sin costo'}
                      {c.note ? ` · ${c.note}` : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="eval-custom-understand">
              Personalizado · entender (proveedor:modelo)
            </Label>
            <Input
              id="eval-custom-understand"
              value={custom.understand}
              onChange={(e) => setCustom((c) => ({ ...c, understand: e.target.value }))}
              placeholder="anthropic:claude-haiku-4-5-20251001"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="eval-custom-reply">Personalizado · redactar (proveedor:modelo)</Label>
            <Input
              id="eval-custom-reply"
              value={custom.reply}
              onChange={(e) => setCustom((c) => ({ ...c, reply: e.target.value }))}
              placeholder="compat:llama-3.3-70b-versatile"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {totalUsd > 0 && (
            <label className="flex items-center gap-2 text-sm" data-testid="eval-accept-cost">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              Entiendo que esta corrida usa modelos pagados (aprox. {money(totalUsd)})
            </label>
          )}
          <Button
            type="button"
            onClick={() => void run()}
            disabled={!projectId || chosen.length === 0 || needsOk || create.isPending}
            data-testid="eval-run"
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FlaskConical className="h-4 w-4" />
            )}{' '}
            Correr la batería
          </Button>
          <span className="text-muted-foreground text-xs">
            {chosen.length} variante{chosen.length === 1 ? '' : 's'} × {BOT_SCENARIOS.length}{' '}
            escenarios
            {totalUsd > 0 ? ` · aprox. ${money(totalUsd)}` : ' · sin costo'}
          </span>
        </div>

        {detail && <EvaluationResults evaluation={detail} />}

        {evaluations && evaluations.items.length > 0 && (
          <div className="space-y-1" data-testid="eval-history">
            <p className="text-sm font-medium">Corridas anteriores</p>
            <ul className="divide-y rounded-md border text-sm">
              {evaluations.items.map((e) => (
                <li
                  key={e.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                >
                  <button
                    type="button"
                    className={`text-left ${e.id === detailId ? 'font-medium' : ''}`}
                    onClick={() => setOpenId(e.id)}
                  >
                    {fmt.dateTime(e.createdAt)} · {e.projectName} ·{' '}
                    {e.variants.map((v) => v.label).join(', ')}
                    {e.createdByName ? ` · ${e.createdByName}` : ''}
                  </button>
                  <span className="flex items-center gap-2">
                    <StatusBadge e={e} />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label="Eliminar corrida"
                      onClick={() =>
                        remove
                          .mutateAsync(e.id)
                          .then(() => {
                            if (openId === e.id) setOpenId(null);
                            toast.success('Corrida eliminada');
                          })
                          .catch((err: Error) => toast.error('No se pudo eliminar', err.message))
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ e }: { e: BotEvaluationDto }) {
  const tone = e.status === 'OK' ? 'success' : e.status === 'ERROR' ? 'danger' : 'warning';
  return (
    <Badge tone={tone} data-testid="eval-status">
      {e.status === 'EN_PROCESO' && <Loader2 className="h-3 w-3 animate-spin" />}
      {BOT_EVALUATION_STATUS_LABELS[e.status]}
      {e.status === 'EN_PROCESO' || e.status === 'PENDIENTE' ? ` ${e.progress}/${e.total}` : ''}
    </Badge>
  );
}

const modelText = (m: BotModelInfo | null) =>
  !m ? 'reglas y plantillas' : `${m.model}${m.fallbackToFake ? ' (simulado)' : ''}`;

function EvaluationResults({ evaluation }: { evaluation: BotEvaluationDto }) {
  const [variantKey, setVariantKey] = useState<string | null>(null);
  const results = evaluation.results ?? [];
  const current: BotEvaluationVariantResult | undefined =
    results.find((r) => r.key === variantKey) ?? results[0];
  if (evaluation.status !== 'OK') {
    return (
      <div className="rounded-md border p-3 text-sm" data-testid="eval-running">
        <p className="flex items-center gap-2">
          <StatusBadge e={evaluation} />
          {evaluation.status === 'ERROR'
            ? (evaluation.error ?? 'La corrida falló')
            : `Corriendo la batería: ${evaluation.progress} de ${evaluation.total} conversaciones…`}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="eval-results">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left text-xs">
            <tr>
              <th className="py-1 pr-3">Variante</th>
              <th className="py-1 pr-3">Modelos (entender / redactar)</th>
              <th className="py-1 pr-3">Escenarios en verde</th>
              <th className="py-1 pr-3">Comprobaciones</th>
              <th className="py-1 pr-3">Respuestas descartadas</th>
              <th className="py-1 pr-3">Costo</th>
              <th className="py-1 pr-3">Por conversación</th>
              <th className="py-1 pr-3">Tiempo por turno</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r: BotEvaluationVariantSummary) => (
              <tr
                key={r.key}
                className={`border-t ${current?.key === r.key ? 'bg-muted/50' : ''}`}
                data-testid="eval-row"
              >
                <td className="py-1.5 pr-3">
                  <button type="button" className="underline" onClick={() => setVariantKey(r.key)}>
                    {r.label}
                  </button>
                  {r.fallbackToFake && (
                    <Badge tone="warning" className="ml-1">
                      simulado
                    </Badge>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-xs">
                  {modelText(r.models.understand)} / {modelText(r.models.reply)}
                </td>
                <td className="py-1.5 pr-3">
                  <span className="font-medium">
                    {r.passedScenarios} de {r.scenarios}
                  </span>
                </td>
                <td className="py-1.5 pr-3">
                  {r.checksPassed} de {r.checksTotal}
                </td>
                <td className="py-1.5 pr-3">{r.verificationFailures}</td>
                <td className="py-1.5 pr-3">{money(r.costUsd)}</td>
                <td className="py-1.5 pr-3">{money(r.costPerConversation)}</td>
                <td className="py-1.5 pr-3">{r.avgLatencyMs} ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {current && <ScenarioList variant={current} />}
    </div>
  );
}

function ScenarioList({ variant }: { variant: BotEvaluationVariantResult }) {
  const [open, setOpen] = useState<string | null>(null);
  const groups = [...new Set(variant.scenarios_detail.map((s) => s.group))] as ScenarioGroup[];
  return (
    <div className="space-y-2" data-testid="eval-scenarios">
      <p className="text-sm font-medium">Escenarios · {variant.label}</p>
      {groups.map((g) => (
        <div key={g}>
          <p className="text-muted-foreground text-xs uppercase">{SCENARIO_GROUP_LABELS[g] ?? g}</p>
          <ul className="divide-y rounded-md border">
            {variant.scenarios_detail
              .filter((s) => s.group === g)
              .map((s) => (
                <li key={s.id} className="px-3 py-1.5 text-sm" data-testid="eval-scenario">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 text-left"
                    onClick={() => setOpen(open === s.id ? null : s.id)}
                  >
                    <span>{s.title}</span>
                    <Badge tone={s.failures.length ? 'danger' : 'success'}>
                      {s.passed} de {s.total}
                    </Badge>
                  </button>
                  {open === s.id && <ScenarioDetail s={s} />}
                </li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ScenarioDetail({ s }: { s: BotEvaluationScenarioDto }) {
  return (
    <div className="mt-2 space-y-1 text-xs" data-testid="eval-scenario-detail">
      {s.failures.length > 0 && (
        <ul className="list-disc pl-4 text-red-700">
          {s.failures.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      )}
      <ol className="space-y-1">
        {s.turns.map((t, i) => (
          <li key={i} className="rounded-md border p-2">
            {t.customer && (
              <p>
                <span className="text-muted-foreground">Cliente:</span> {t.customer}
                {t.intent && (
                  <span className="text-muted-foreground">
                    {' '}
                    · entendió{' '}
                    {BOT_INTENT_LABELS[t.intent as keyof typeof BOT_INTENT_LABELS] ?? t.intent}
                  </span>
                )}
              </p>
            )}
            <p>
              <span className="text-muted-foreground">Bot:</span>{' '}
              {t.reply ? (
                <span className="whitespace-pre-line">{t.reply}</span>
              ) : (
                <em>(no respondió)</em>
              )}
            </p>
            <p className="text-muted-foreground">
              {BOT_ACTION_LABELS[t.action as keyof typeof BOT_ACTION_LABELS] ?? t.action}
              {t.priority ? ' · prioridad' : ''} · {money(t.costUsd)} · {t.latencyMs} ms
              {!t.verified && t.reply ? ' · no pasó la verificación' : ''}
              {t.issues.length ? ` · ${t.issues.join('; ')}` : ''}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}
